// src/pages/UncategorisedShops.js

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import axios from "axios";

/* =========================================================
   APIs
========================================================= */

/*
 * Add the deployed Function keys here.
 */

const GET_UNCATEGORISED_SHOPS_API =
    "https://oppiesfunc.azurewebsites.net/api/getUncategorisedShops?code=XjPyH1nen4X8cbygPV5_iCtSl9csh0fTinCzUbc2u55WAzFuWGmzGg==";

const UPDATE_SHOP_COUNTRY_API =
    "https://oppiesfunc.azurewebsites.net/api/updateShopCountryAdmin?code=TFbUaO-wKDJFxlN1mO-AEUryN6Zd7J5H_jVcU0iHNNxrAzFuACSB6A==";

/*
 * Existing OPPIES address search Function.
 *
 * Same address-search service used by ShopImporter.
 */

const SEARCH_ADDRESS_API =
    "https://oppiesfunc.azurewebsites.net/api/searchShopAddresses?code=X0-m7ol4AEMcwVG0HdYiAidPss9PkGoZdcYTC8aOCn2SAzFuuLkOKw==";


/* =========================================================
   COUNTRY OPTIONS
========================================================= */

const COUNTRIES = [
    {
        code: "NZ",
        name: "New Zealand",
    },
    {
        code: "AU",
        name: "Australia",
    },
];


/* =========================================================
   HELPERS
========================================================= */

function cleanString(value) {
    return String(
        value || ""
    ).trim();
}


function numberOrBlank(value) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "";
    }

    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : "";
}


function normaliseSuggestions(data) {
    const source =
        data?.suggestions ||
        data?.predictions ||
        data?.results ||
        [];

    if (!Array.isArray(source)) {
        return [];
    }

    return source
        .map((item) => {

            const location =
                item?.location ||
                item?.geometry
                    ?.location ||
                {};

            return {
                placeId:
                    item.placeId ||
                    item.place_id ||
                    item.id ||
                    "",

                description:
                    item.description ||
                    item.formattedAddress ||
                    item.formatted_address ||
                    item.address ||
                    item.text ||
                    "",

                mainText:
                    item.mainText ||
                    item.main_text ||
                    item
                        ?.structured_formatting
                        ?.main_text ||
                    "",

                secondaryText:
                    item.secondaryText ||
                    item.secondary_text ||
                    item
                        ?.structured_formatting
                        ?.secondary_text ||
                    "",

                latitude:
                    item.latitude ??
                    item.lat ??
                    location.lat ??
                    null,

                longitude:
                    item.longitude ??
                    item.lng ??
                    location.lng ??
                    null,

                country:
                    item.country ||
                    item.countryCode ||
                    item.country_code ||
                    "",

                countryName:
                    item.countryName ||
                    item.country_name ||
                    "",
            };

        })
        .filter(
            (item) =>
                item.description
        );
}


function countryFromAddress(value) {
    const text =
        cleanString(value)
            .toLowerCase();

    if (
        text.includes(
            "new zealand"
        )
    ) {
        return {
            code: "NZ",
            name: "New Zealand",
        };
    }

    if (
        text.includes(
            "australia"
        )
    ) {
        return {
            code: "AU",
            name: "Australia",
        };
    }

    return null;
}


/* =========================================================
   COMPONENT
========================================================= */

export default function UncategorisedShops() {

    /* =====================================================
       DATA
    ===================================================== */

    const [
        shops,
        setShops,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        loadError,
        setLoadError,
    ] = useState("");


    /* =====================================================
       SEARCH
    ===================================================== */

    const [
        search,
        setSearch,
    ] = useState("");


    /* =====================================================
       SELECTED SHOP
    ===================================================== */

    const [
        selectedShopId,
        setSelectedShopId,
    ] = useState("");

    const selectedShop =
        useMemo(() => {

            if (!selectedShopId) {
                return null;
            }

            return (
                shops.find(
                    (shop) =>
                        shop.shopId ===
                        selectedShopId
                ) ||
                null
            );

        }, [
            shops,
            selectedShopId,
        ]);


    /* =====================================================
       EDIT FORM
    ===================================================== */

    const [
        form,
        setForm,
    ] = useState({
        address: "",
        country: "",
        countryName: "",
        latitude: "",
        longitude: "",
    });


    /* =====================================================
       ADDRESS AUTOCOMPLETE
    ===================================================== */

    const addressWrapperRef =
        useRef(null);

    const searchAbortRef =
        useRef(null);

    const [
        suggestions,
        setSuggestions,
    ] = useState([]);

    const [
        addressSearching,
        setAddressSearching,
    ] = useState(false);

    const [
        showSuggestions,
        setShowSuggestions,
    ] = useState(false);

    const [
        addressWasSelected,
        setAddressWasSelected,
    ] = useState(false);


    /* =====================================================
       SAVE
    ===================================================== */

    const [
        saving,
        setSaving,
    ] = useState(false);

    const [
        status,
        setStatus,
    ] = useState({
        type: "",
        message: "",
    });


    /* =====================================================
       LOAD UNCATEGORISED SHOPS
    ===================================================== */

    const fetchShops =
        useCallback(
            async () => {

                try {
                    setLoading(true);

                    setLoadError("");

                    const response =
                        await axios.get(
                            GET_UNCATEGORISED_SHOPS_API,
                            {
                                timeout:
                                    30000,

                                headers: {
                                    Accept:
                                        "application/json",
                                },
                            }
                        );

                    const list =
                        Array.isArray(
                            response
                                ?.data
                                ?.shops
                        )
                            ? response
                                .data
                                .shops
                            : [];


                    setShops(
                        list
                    );


                    /*
                     * If currently selected shop has
                     * disappeared because it was just
                     * categorised, clear selection.
                     */

                    setSelectedShopId(
                        (current) => {

                            if (!current) {
                                return "";
                            }

                            return list.some(
                                (shop) =>
                                    shop.shopId ===
                                    current
                            )
                                ? current
                                : "";
                        }
                    );

                } catch (error) {

                    console.error(
                        "❌ getUncategorisedShops failed:",
                        error?.response?.data ||
                        error?.message ||
                        error
                    );

                    setLoadError(
                        error
                            ?.response
                            ?.data
                            ?.error ||
                        "Could not load uncategorised shops."
                    );

                } finally {

                    setLoading(false);

                }

            },
            []
        );


    useEffect(() => {

        fetchShops();

    }, [
        fetchShops,
    ]);


    /* =====================================================
       WHEN SHOP SELECTED
    ===================================================== */

    useEffect(() => {

        if (!selectedShop) {

            setForm({
                address: "",
                country: "",
                countryName: "",
                latitude: "",
                longitude: "",
            });

            return;
        }


        setForm({
            address:
                selectedShop.address ||
                "",

            country:
                selectedShop.country ||
                "",

            countryName:
                selectedShop.countryName ||
                "",

            latitude:
                numberOrBlank(
                    selectedShop
                        ?.location
                        ?.lat ??
                    selectedShop
                        ?.latitude
                ),

            longitude:
                numberOrBlank(
                    selectedShop
                        ?.location
                        ?.lng ??
                    selectedShop
                        ?.longitude
                ),
        });


        setSuggestions([]);

        setShowSuggestions(false);

        setAddressWasSelected(false);

        setStatus({
            type: "",
            message: "",
        });

    }, [
        selectedShop,
    ]);


    /* =====================================================
       FILTERED SHOPS
    ===================================================== */

    const filteredShops =
        useMemo(() => {

            const term =
                search
                    .trim()
                    .toLowerCase();

            if (!term) {
                return shops;
            }

            return shops.filter(
                (shop) => {

                    const haystack = [
                        shop.name,
                        shop.shopId,
                        shop.email,
                        shop.address,
                        shop.suburb,
                        shop.city,
                        shop.region,
                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();

                    return haystack.includes(
                        term
                    );
                }
            );

        }, [
            shops,
            search,
        ]);


    /* =====================================================
       ADDRESS SEARCH
    ===================================================== */

    useEffect(() => {

        const query =
            form.address
                .trim();

        if (
            !selectedShop ||
            addressWasSelected ||
            query.length < 3
        ) {
            setSuggestions([]);

            setAddressSearching(
                false
            );

            return;
        }


        const timer =
            setTimeout(
                async () => {

                    if (
                        searchAbortRef
                            .current
                    ) {
                        searchAbortRef
                            .current
                            .abort();
                    }


                    const controller =
                        new AbortController();

                    searchAbortRef.current =
                        controller;


                    try {

                        setAddressSearching(
                            true
                        );


                        const response =
                            await fetch(
                                `${SEARCH_ADDRESS_API}&query=${encodeURIComponent(
                                    query
                                )}`,
                                {
                                    method:
                                        "GET",

                                    headers: {
                                        Accept:
                                            "application/json",
                                    },

                                    signal:
                                        controller.signal,
                                }
                            );


                        let data =
                            null;

                        try {

                            data =
                                await response
                                    .json();

                        } catch {

                            data =
                                null;

                        }


                        if (!response.ok) {

                            throw new Error(
                                data?.error ||
                                `Address search failed (${response.status}).`
                            );

                        }


                        const items =
                            normaliseSuggestions(
                                data
                            );


                        setSuggestions(
                            items
                        );

                        setShowSuggestions(
                            items.length > 0
                        );

                    } catch (error) {

                        if (
                            error?.name !==
                            "AbortError"
                        ) {

                            console.error(
                                "❌ Address search failed:",
                                error
                            );

                        }

                    } finally {

                        if (
                            !controller
                                .signal
                                .aborted
                        ) {
                            setAddressSearching(
                                false
                            );
                        }

                    }

                },
                350
            );


        return () =>
            clearTimeout(
                timer
            );

    }, [
        form.address,
        selectedShop,
        addressWasSelected,
    ]);


    /* =====================================================
       CLICK OUTSIDE AUTOCOMPLETE
    ===================================================== */

    useEffect(() => {

        const handler =
            (event) => {

                if (
                    addressWrapperRef
                        .current &&
                    !addressWrapperRef
                        .current
                        .contains(
                            event.target
                        )
                ) {
                    setShowSuggestions(
                        false
                    );
                }

            };


        document.addEventListener(
            "mousedown",
            handler
        );


        return () => {

            document.removeEventListener(
                "mousedown",
                handler
            );

        };

    }, []);


    /* =====================================================
       SELECT ADDRESS
    ===================================================== */

    const selectAddress =
        (suggestion) => {

            const detectedCountry =
                (
                    suggestion.country &&
                    suggestion.countryName
                )
                    ? {
                        code:
                            String(
                                suggestion.country
                            )
                                .trim()
                                .toUpperCase(),

                        name:
                            suggestion.countryName,
                    }
                    : countryFromAddress(
                        suggestion.description
                    );


            setForm(
                (current) => ({
                    ...current,

                    address:
                        suggestion.description,

                    latitude:
                        suggestion.latitude ??
                        current.latitude,

                    longitude:
                        suggestion.longitude ??
                        current.longitude,

                    country:
                        detectedCountry
                            ?.code ||
                        current.country,

                    countryName:
                        detectedCountry
                            ?.name ||
                        current.countryName,
                })
            );


            setSuggestions([]);

            setShowSuggestions(false);

            setAddressWasSelected(
                true
            );


            setStatus({
                type: "info",

                message:
                    detectedCountry
                        ? `${detectedCountry.name} detected from the selected address. Please confirm and save.`
                        : "Address selected. Please confirm the country before saving.",
            });

        };


    /* =====================================================
       COUNTRY CHANGE
    ===================================================== */

    const handleCountryChange =
        (event) => {

            const code =
                event.target.value;


            const country =
                COUNTRIES.find(
                    (item) =>
                        item.code ===
                        code
                );


            setForm(
                (current) => ({
                    ...current,

                    country:
                        country?.code ||
                        "",

                    countryName:
                        country?.name ||
                        "",
                })
            );


            setStatus({
                type: "",
                message: "",
            });

        };


    /* =====================================================
       VALIDATION
    ===================================================== */

    const canSave =
        !!selectedShop &&
        !!form.country &&
        !!form.countryName &&
        !saving;


    /* =====================================================
       SAVE
    ===================================================== */

    const handleSave =
        async () => {

            if (!selectedShop) {

                setStatus({
                    type: "error",
                    message:
                        "Select an Op Shop first.",
                });

                return;
            }


            if (
                !form.country ||
                !form.countryName
            ) {

                setStatus({
                    type: "error",
                    message:
                        "Choose New Zealand or Australia.",
                });

                return;
            }


            /*
             * Coordinates are optional.
             *
             * But if one is supplied, both must be.
             */

            const hasLat =
                form.latitude !==
                "";

            const hasLng =
                form.longitude !==
                "";


            if (
                hasLat !==
                hasLng
            ) {

                setStatus({
                    type: "error",
                    message:
                        "Latitude and longitude must both be supplied.",
                });

                return;
            }


            try {

                setSaving(true);

                setStatus({
                    type: "info",
                    message:
                        "Saving shop country...",
                });


                const payload = {
                    shopId:
                        selectedShop.shopId,

                    /*
                     * REQUIRED:
                     * shops partition key = /email
                     */
                    email:
                        selectedShop.email,

                    address:
                        form.address.trim(),

                    country:
                        form.country,

                    countryName:
                        form.countryName,

                    latitude:
                        hasLat
                            ? Number(
                                form.latitude
                            )
                            : null,

                    longitude:
                        hasLng
                            ? Number(
                                form.longitude
                            )
                            : null,
                };


                console.log(
                    "📤 updateShopCountryAdmin:",
                    payload
                );


                await axios.post(
                    UPDATE_SHOP_COUNTRY_API,
                    payload,
                    {
                        timeout:
                            30000,
                    }
                );


                const correctedName =
                    selectedShop.name ||
                    selectedShop.shopId;


                /*
                 * Immediately remove from UI.
                 *
                 * It no longer belongs in this queue.
                 */

                setShops(
                    (current) =>
                        current.filter(
                            (shop) =>
                                shop.shopId !==
                                selectedShop.shopId
                        )
                );


                setSelectedShopId(
                    ""
                );


                setStatus({
                    type: "success",

                    message:
                        `${correctedName} has been categorised as ${form.countryName}.`,
                });


            } catch (error) {

                console.error(
                    "❌ updateShopCountryAdmin failed:",
                    error?.response?.data ||
                    error?.message ||
                    error
                );


                setStatus({
                    type: "error",

                    message:
                        error
                            ?.response
                            ?.data
                            ?.error ||
                        "Could not update this shop.",
                });


            } finally {

                setSaving(false);

            }

        };


    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <>
            <style>
                {pageStyles}
            </style>

            <div className="uncat-page">

                {/* ==========================================
                    HEADER
                ========================================== */}

                <section className="uncat-header">

                    <div>

                        <div className="uncat-eyebrow">
                            Op Shops
                        </div>

                        <h1>
                            Uncategorised Shops
                        </h1>

                        <p>
                            Review imported Op Shops that
                            do not yet have a country
                            assigned. Correct the address
                            where necessary and assign the
                            shop to New Zealand or
                            Australia.
                        </p>

                    </div>


                    <div className="uncat-total">

                        <strong>
                            {shops.length}
                        </strong>

                        <span>
                            remaining
                        </span>

                    </div>

                </section>


                {/* ==========================================
                    STATUS
                ========================================== */}

                {status.message && (

                    <div
                        className={
                            `uncat-status uncat-status-${status.type}`
                        }
                    >

                        <span className="uncat-status-icon">

                            {status.type ===
                            "success"
                                ? "✓"
                                : status.type ===
                                  "error"
                                    ? "!"
                                    : "i"
                            }

                        </span>

                        {status.message}

                    </div>

                )}


                {loadError && (

                    <div className="uncat-status uncat-status-error">

                        <span className="uncat-status-icon">
                            !
                        </span>

                        <span>
                            {loadError}
                        </span>

                        <button
                            type="button"
                            onClick={
                                fetchShops
                            }
                        >
                            Retry
                        </button>

                    </div>

                )}


                {/* ==========================================
                    MAIN GRID
                ========================================== */}

                <div className="uncat-layout">

                    {/* ======================================
                        SHOP QUEUE
                    ====================================== */}

                    <section className="uncat-list-panel">

                        <div className="uncat-panel-header">

                            <div>

                                <span>
                                    Cleanup Queue
                                </span>

                                <h2>
                                    Shops needing a country
                                </h2>

                            </div>

                        </div>


                        <div className="uncat-search">

                            <svg
                                viewBox="0 0 24 24"
                                width="16"
                                height="16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle
                                    cx="11"
                                    cy="11"
                                    r="8"
                                />

                                <path d="m21 21-4.35-4.35" />

                            </svg>

                            <input
                                value={search}
                                onChange={
                                    (event) =>
                                        setSearch(
                                            event
                                                .target
                                                .value
                                        )
                                }
                                placeholder="Search name, address, shop ID..."
                            />

                        </div>


                        <div className="uncat-list">

                            {loading ? (

                                <div className="uncat-empty">
                                    Loading uncategorised shops...
                                </div>

                            ) : shops.length === 0 ? (

                                <div className="uncat-complete">

                                    <div className="uncat-complete-icon">
                                        ✓
                                    </div>

                                    <strong>
                                        All caught up
                                    </strong>

                                    <span>
                                        Every Op Shop currently
                                        has a country assigned.
                                    </span>

                                </div>

                            ) : filteredShops.length ===
                              0 ? (

                                <div className="uncat-empty">
                                    No shops match your search.
                                </div>

                            ) : (

                                filteredShops.map(
                                    (shop) => {

                                        const selected =
                                            shop.shopId ===
                                            selectedShopId;


                                        return (
                                            <button
                                                key={
                                                    shop.shopId
                                                }
                                                type="button"
                                                className={
                                                    `uncat-shop ${
                                                        selected
                                                            ? "uncat-shop-selected"
                                                            : ""
                                                    }`
                                                }
                                                onClick={() =>
                                                    setSelectedShopId(
                                                        shop.shopId
                                                    )
                                                }
                                            >

                                                <div className="uncat-shop-top">

                                                    <strong>
                                                        {shop.name ||
                                                            shop.shopId}
                                                    </strong>

                                                    <span className="uncat-badge">
                                                        Uncategorised
                                                    </span>

                                                </div>


                                                <div className="uncat-shop-address">

                                                    {shop.address ||
                                                        "No address recorded"}

                                                </div>


                                                <div className="uncat-shop-meta">

                                                    <span>
                                                        {shop.shopId}
                                                    </span>

                                                    {shop.location
                                                        ?.lat !=
                                                        null &&
                                                        shop.location
                                                            ?.lng !=
                                                            null && (

                                                        <span>
                                                            {
                                                                shop.location
                                                                    .lat
                                                            }
                                                            ,{" "}
                                                            {
                                                                shop.location
                                                                    .lng
                                                            }
                                                        </span>

                                                    )}

                                                </div>


                                                {Array.isArray(
                                                    shop.issues
                                                ) &&
                                                    shop.issues.length >
                                                    0 && (

                                                    <div className="uncat-issues">

                                                        {shop.issues.map(
                                                            (
                                                                issue
                                                            ) => (

                                                                <span
                                                                    key={
                                                                        issue
                                                                    }
                                                                >
                                                                    {issue ===
                                                                    "missing_country"
                                                                        ? "Missing country code"
                                                                        : issue ===
                                                                          "missing_country_name"
                                                                            ? "Missing country name"
                                                                            : issue}
                                                                </span>

                                                            )
                                                        )}

                                                    </div>

                                                )}

                                            </button>
                                        );

                                    }
                                )

                            )}

                        </div>

                    </section>


                    {/* ======================================
                        EDITOR
                    ====================================== */}

                    <section className="uncat-editor">

                        {!selectedShop ? (

                            <div className="uncat-editor-empty">

                                <div className="uncat-editor-icon">

                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.7"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />

                                        <circle
                                            cx="12"
                                            cy="10"
                                            r="2.5"
                                        />
                                    </svg>

                                </div>

                                <strong>
                                    Select an Op Shop
                                </strong>

                                <span>
                                    Choose a shop from the
                                    cleanup queue to review
                                    its address and assign a
                                    country.
                                </span>

                            </div>

                        ) : (

                            <>

                                <div className="uncat-editor-header">

                                    <span>
                                        Categorise Shop
                                    </span>

                                    <h2>
                                        {selectedShop.name ||
                                            selectedShop.shopId}
                                    </h2>

                                    <small>
                                        {selectedShop.shopId}
                                    </small>

                                </div>


                                {/* ==========================
                                    CURRENT DATA
                                ========================== */}

                                <div className="uncat-current">

                                    <div>

                                        <span>
                                            Current address
                                        </span>

                                        <strong>
                                            {selectedShop.address ||
                                                "Not recorded"}
                                        </strong>

                                    </div>


                                    <div>

                                        <span>
                                            Current coordinates
                                        </span>

                                        <strong>

                                            {selectedShop.location
                                                ?.lat != null &&
                                            selectedShop.location
                                                ?.lng != null
                                                ? `${selectedShop.location.lat}, ${selectedShop.location.lng}`
                                                : "Not recorded"}

                                        </strong>

                                    </div>

                                </div>


                                {/* ==========================
                                    ADDRESS
                                ========================== */}

                                <div
                                    className="uncat-field"
                                    ref={
                                        addressWrapperRef
                                    }
                                >

                                    <label>
                                        Address
                                    </label>

                                    <div className="uncat-address-wrap">

                                        <input
                                            type="text"
                                            value={
                                                form.address
                                            }
                                            onChange={
                                                (event) => {

                                                    setForm(
                                                        (
                                                            current
                                                        ) => ({
                                                            ...current,

                                                            address:
                                                                event
                                                                    .target
                                                                    .value,
                                                        })
                                                    );

                                                    setAddressWasSelected(
                                                        false
                                                    );

                                                    setStatus({
                                                        type: "",
                                                        message: "",
                                                    });

                                                }
                                            }
                                            onFocus={() => {

                                                if (
                                                    suggestions.length >
                                                    0
                                                ) {
                                                    setShowSuggestions(
                                                        true
                                                    );
                                                }

                                            }}
                                            placeholder="Start typing the shop address..."
                                            autoComplete="off"
                                        />


                                        {addressSearching && (

                                            <span className="uncat-searching">
                                                …
                                            </span>

                                        )}

                                    </div>


                                    {showSuggestions &&
                                        suggestions.length >
                                        0 && (

                                        <div className="uncat-suggestions">

                                            {suggestions.map(
                                                (
                                                    suggestion,
                                                    index
                                                ) => (

                                                    <button
                                                        key={
                                                            suggestion.placeId ||
                                                            `${suggestion.description}-${index}`
                                                        }
                                                        type="button"
                                                        onClick={() =>
                                                            selectAddress(
                                                                suggestion
                                                            )
                                                        }
                                                    >

                                                        <strong>
                                                            {suggestion.mainText ||
                                                                suggestion.description}
                                                        </strong>

                                                        {suggestion.secondaryText && (

                                                            <span>
                                                                {
                                                                    suggestion.secondaryText
                                                                }
                                                            </span>

                                                        )}

                                                    </button>

                                                )
                                            )}

                                        </div>

                                    )}

                                    <small>
                                        Select an autocomplete
                                        result where possible
                                        so the address is
                                        standardised.
                                    </small>

                                </div>


                                {/* ==========================
                                    COUNTRY
                                ========================== */}

                                <div className="uncat-field">

                                    <label>
                                        Country
                                    </label>

                                    <select
                                        value={
                                            form.country
                                        }
                                        onChange={
                                            handleCountryChange
                                        }
                                    >

                                        <option value="">
                                            Select country...
                                        </option>

                                        {COUNTRIES.map(
                                            (
                                                country
                                            ) => (

                                                <option
                                                    key={
                                                        country.code
                                                    }
                                                    value={
                                                        country.code
                                                    }
                                                >
                                                    {
                                                        country.name
                                                    }
                                                </option>

                                            )
                                        )}

                                    </select>

                                </div>


                                {/* ==========================
                                    COORDINATES
                                ========================== */}

                                <div className="uncat-coordinate-grid">

                                    <div className="uncat-field">

                                        <label>
                                            Latitude
                                        </label>

                                        <input
                                            type="number"
                                            step="any"
                                            value={
                                                form.latitude
                                            }
                                            onChange={
                                                (event) =>
                                                    setForm(
                                                        (
                                                            current
                                                        ) => ({
                                                            ...current,

                                                            latitude:
                                                                event
                                                                    .target
                                                                    .value,
                                                        })
                                                    )
                                            }
                                            placeholder="-41.2865"
                                        />

                                    </div>


                                    <div className="uncat-field">

                                        <label>
                                            Longitude
                                        </label>

                                        <input
                                            type="number"
                                            step="any"
                                            value={
                                                form.longitude
                                            }
                                            onChange={
                                                (event) =>
                                                    setForm(
                                                        (
                                                            current
                                                        ) => ({
                                                            ...current,

                                                            longitude:
                                                                event
                                                                    .target
                                                                    .value,
                                                        })
                                                    )
                                            }
                                            placeholder="174.7762"
                                        />

                                    </div>

                                </div>


                                {/* ==========================
                                    RESULT PREVIEW
                                ========================== */}

                                {form.country && (

                                    <div className="uncat-result">

                                        <span>
                                            This shop will be
                                            categorised as
                                        </span>

                                        <strong>
                                            {
                                                form.countryName
                                            }
                                        </strong>

                                        <small>
                                            {form.country}
                                        </small>

                                    </div>

                                )}


                                {/* ==========================
                                    ACTION
                                ========================== */}

                                <div className="uncat-actions">

                                    <button
                                        type="button"
                                        className="uncat-cancel"
                                        onClick={() =>
                                            setSelectedShopId(
                                                ""
                                            )
                                        }
                                        disabled={
                                            saving
                                        }
                                    >
                                        Cancel
                                    </button>


                                    <button
                                        type="button"
                                        className="uncat-save"
                                        onClick={
                                            handleSave
                                        }
                                        disabled={
                                            !canSave
                                        }
                                    >

                                        {saving
                                            ? "Saving..."
                                            : "Save & Categorise"}

                                    </button>

                                </div>

                            </>

                        )}

                    </section>

                </div>

            </div>
        </>
    );
}


/* =========================================================
   STYLES
========================================================= */

const pageStyles = `

.uncat-page {
    max-width: 1280px;
    margin: 0 auto;
    padding: 28px 30px 60px;

    color: #302f2b;

    font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
}


/* ========================================================
   HEADER
======================================================== */

.uncat-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 25px;

    margin-bottom: 24px;
}

.uncat-eyebrow,
.uncat-panel-header span,
.uncat-editor-header > span {
    color: #047857;

    font-size: 10px;
    font-weight: 850;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.uncat-header h1 {
    margin: 6px 0 7px;

    color: #17211c;

    font-size: clamp(
        28px,
        3vw,
        38px
    );

    line-height: 1.08;
    letter-spacing: -0.035em;
}

.uncat-header p {
    max-width: 700px;

    margin: 0;

    color: #6f7973;

    font-size: 14px;
    line-height: 1.6;
}

.uncat-total {
    display: flex;
    min-width: 100px;

    flex-direction: column;
    align-items: center;

    padding: 13px 18px;

    border: 1px solid #e5d9bf;
    border-radius: 15px;

    background: #fbf7ed;
}

.uncat-total strong {
    color: #047857;

    font-size: 25px;
    line-height: 1;
}

.uncat-total span {
    margin-top: 4px;

    color: #8b7f69;

    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}


/* ========================================================
   STATUS
======================================================== */

.uncat-status {
    display: flex;
    align-items: center;
    gap: 9px;

    margin-bottom: 18px;
    padding: 11px 13px;

    border: 1px solid #e5dfd3;
    border-radius: 11px;

    background: #ffffff;

    color: #5f665f;

    font-size: 11px;
    font-weight: 650;
}

.uncat-status-icon {
    display: grid;

    width: 23px;
    height: 23px;

    flex: 0 0 auto;

    place-items: center;

    border-radius: 50%;

    background: #f2f4f2;

    font-weight: 900;
}

.uncat-status button {
    margin-left: auto;

    border: none;

    background: transparent;

    color: inherit;

    cursor: pointer;

    font-size: 10px;
    font-weight: 850;
}

.uncat-status-success {
    border-color: #bfe2cc;

    background: #effaf3;

    color: #047857;
}

.uncat-status-error {
    border-color: #f2c8c4;

    background: #fff3f2;

    color: #a13c34;
}

.uncat-status-info {
    border-color: #e4dabf;

    background: #fffaf0;

    color: #75613e;
}


/* ========================================================
   LAYOUT
======================================================== */

.uncat-layout {
    display: grid;

    grid-template-columns:
        minmax(320px, 0.8fr)
        minmax(430px, 1.25fr);

    gap: 18px;

    align-items: start;
}


/* ========================================================
   PANELS
======================================================== */

.uncat-list-panel,
.uncat-editor {
    border: 1px solid #e7e2d9;

    border-radius: 18px;

    background: #ffffff;

    box-shadow:
        0 8px 22px
        rgba(
            28,
            35,
            31,
            0.035
        );
}

.uncat-list-panel {
    overflow: hidden;
}

.uncat-editor {
    padding: 21px;

    position: sticky;

    top: 20px;
}


/* ========================================================
   LIST HEADER
======================================================== */

.uncat-panel-header {
    padding: 18px 18px 12px;
}

.uncat-panel-header h2 {
    margin: 4px 0 0;

    color: #29332d;

    font-size: 17px;
    letter-spacing: -0.025em;
}


/* ========================================================
   SEARCH
======================================================== */

.uncat-search {
    display: flex;
    align-items: center;
    gap: 8px;

    margin: 0 15px 13px;

    padding: 0 11px;

    border: 1px solid #ddd8cf;
    border-radius: 10px;

    background: #fbfbfa;

    color: #869089;
}

.uncat-search input {
    width: 100%;

    padding: 10px 0;

    border: none;
    outline: none;

    background: transparent;

    color: #344039;

    font-family: inherit;
    font-size: 11px;
}


/* ========================================================
   LIST
======================================================== */

.uncat-list {
    max-height: calc(100vh - 280px);

    overflow-y: auto;

    border-top: 1px solid #eeeae4;
}

.uncat-shop {
    display: block;

    width: 100%;

    padding: 14px 16px;

    border: none;
    border-bottom: 1px solid #eeeae4;

    background: #ffffff;

    cursor: pointer;

    font-family: inherit;
    text-align: left;

    transition:
        background 0.16s ease,
        box-shadow 0.16s ease;
}

.uncat-shop:hover {
    background: #fbfcfb;
}

.uncat-shop-selected {
    background: #f2f8f4;

    box-shadow:
        inset 3px 0
        #047857;
}

.uncat-shop-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.uncat-shop-top strong {
    color: #344139;

    font-size: 11px;
    line-height: 1.35;
}

.uncat-badge {
    flex: 0 0 auto;

    padding: 3px 6px;

    border-radius: 999px;

    background: #fff3d9;

    color: #8d6723;

    font-size: 6px;
    font-weight: 850;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.uncat-shop-address {
    margin-top: 5px;

    color: #68736c;

    font-size: 9px;
    line-height: 1.45;
}

.uncat-shop-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;

    margin-top: 6px;

    color: #9a9f9b;

    font-size: 7px;
}

.uncat-issues {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;

    margin-top: 8px;
}

.uncat-issues span {
    padding: 3px 6px;

    border-radius: 6px;

    background: #f4f1ea;

    color: #877b67;

    font-size: 6px;
    font-weight: 750;
}


/* ========================================================
   EMPTY
======================================================== */

.uncat-empty {
    padding: 35px 20px;

    color: #8b938e;

    font-size: 11px;
    text-align: center;
}

.uncat-complete {
    display: flex;

    min-height: 240px;

    flex-direction: column;
    align-items: center;
    justify-content: center;

    padding: 30px;

    text-align: center;
}

.uncat-complete-icon {
    display: grid;

    width: 43px;
    height: 43px;

    margin-bottom: 10px;

    place-items: center;

    border-radius: 50%;

    background: #e8f5ed;

    color: #047857;

    font-size: 18px;
    font-weight: 900;
}

.uncat-complete strong {
    color: #334039;

    font-size: 13px;
}

.uncat-complete span {
    max-width: 230px;

    margin-top: 4px;

    color: #8a938d;

    font-size: 9px;
    line-height: 1.5;
}


/* ========================================================
   EDITOR EMPTY
======================================================== */

.uncat-editor-empty {
    display: flex;

    min-height: 390px;

    flex-direction: column;
    align-items: center;
    justify-content: center;

    text-align: center;
}

.uncat-editor-icon {
    display: grid;

    width: 53px;
    height: 53px;

    margin-bottom: 12px;

    place-items: center;

    border-radius: 15px;

    background: #eef7f1;

    color: #047857;
}

.uncat-editor-empty strong {
    color: #39463f;

    font-size: 13px;
}

.uncat-editor-empty span {
    max-width: 290px;

    margin-top: 5px;

    color: #8a938d;

    font-size: 9px;
    line-height: 1.55;
}


/* ========================================================
   EDITOR HEADER
======================================================== */

.uncat-editor-header {
    margin-bottom: 17px;
}

.uncat-editor-header h2 {
    margin: 4px 0 2px;

    color: #26342d;

    font-size: 20px;
    letter-spacing: -0.03em;
}

.uncat-editor-header small {
    color: #929a95;

    font-size: 8px;
}


/* ========================================================
   CURRENT
======================================================== */

.uncat-current {
    display: grid;

    grid-template-columns:
        repeat(
            2,
            minmax(0, 1fr)
        );

    gap: 8px;

    margin-bottom: 18px;
}

.uncat-current > div {
    padding: 10px;

    border: 1px solid #e9e4da;
    border-radius: 10px;

    background: #fbf8f1;
}

.uncat-current span {
    display: block;

    margin-bottom: 3px;

    color: #958a77;

    font-size: 7px;
    font-weight: 750;
    text-transform: uppercase;
}

.uncat-current strong {
    display: block;

    overflow: hidden;

    color: #554d41;

    font-size: 9px;
    line-height: 1.45;

    text-overflow: ellipsis;
}


/* ========================================================
   FORM
======================================================== */

.uncat-field {
    position: relative;

    margin-bottom: 14px;
}

.uncat-field label {
    display: block;

    margin-bottom: 6px;

    color: #455149;

    font-size: 9px;
    font-weight: 800;
}

.uncat-field input,
.uncat-field select {
    box-sizing: border-box;

    width: 100%;

    min-height: 40px;

    padding: 9px 11px;

    border: 1px solid #ddd8cd;
    border-radius: 9px;

    outline: none;

    background: #ffffff;

    color: #38433d;

    font-family: inherit;
    font-size: 10px;
}

.uncat-field input:focus,
.uncat-field select:focus {
    border-color: #79ac94;

    box-shadow:
        0 0 0 3px
        rgba(
            4,
            120,
            87,
            0.07
        );
}

.uncat-field > small {
    display: block;

    margin-top: 5px;

    color: #9a9f9b;

    font-size: 7px;
    line-height: 1.45;
}


/* ========================================================
   ADDRESS
======================================================== */

.uncat-address-wrap {
    position: relative;
}

.uncat-searching {
    position: absolute;

    top: 50%;
    right: 12px;

    transform:
        translateY(-50%);

    color: #047857;

    font-size: 18px;
}

.uncat-suggestions {
    position: absolute;

    z-index: 30;

    top: 67px;
    left: 0;
    right: 0;

    overflow: hidden;

    border: 1px solid #ddd8cf;
    border-radius: 10px;

    background: #ffffff;

    box-shadow:
        0 10px 26px
        rgba(
            30,
            40,
            34,
            0.12
        );
}

.uncat-suggestions button {
    display: block;

    width: 100%;

    padding: 10px 11px;

    border: none;
    border-bottom: 1px solid #eeeae4;

    background: #ffffff;

    cursor: pointer;

    font-family: inherit;
    text-align: left;
}

.uncat-suggestions button:hover {
    background: #f7faf8;
}

.uncat-suggestions strong {
    display: block;

    color: #425048;

    font-size: 9px;
}

.uncat-suggestions span {
    display: block;

    margin-top: 2px;

    color: #8b948e;

    font-size: 7px;
}


/* ========================================================
   COORDINATES
======================================================== */

.uncat-coordinate-grid {
    display: grid;

    grid-template-columns:
        repeat(
            2,
            minmax(0, 1fr)
        );

    gap: 10px;
}


/* ========================================================
   RESULT
======================================================== */

.uncat-result {
    display: flex;
    align-items: center;
    gap: 8px;

    margin-top: 4px;
    padding: 10px 11px;

    border: 1px solid #bfe1cc;
    border-radius: 10px;

    background: #eff9f2;
}

.uncat-result span {
    color: #6a756e;

    font-size: 8px;
}

.uncat-result strong {
    color: #047857;

    font-size: 9px;
}

.uncat-result small {
    margin-left: auto;

    padding: 3px 6px;

    border-radius: 999px;

    background: #dcefe3;

    color: #047857;

    font-size: 7px;
    font-weight: 850;
}


/* ========================================================
   ACTIONS
======================================================== */

.uncat-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;

    margin-top: 18px;
    padding-top: 16px;

    border-top: 1px solid #eeeae4;
}

.uncat-cancel,
.uncat-save {
    min-height: 38px;

    padding: 8px 13px;

    border-radius: 9px;

    cursor: pointer;

    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
}

.uncat-cancel {
    border: 1px solid #d9d9d5;

    background: #ffffff;

    color: #717872;
}

.uncat-save {
    border: none;

    background: #047857;

    color: #ffffff;

    box-shadow:
        0 6px 14px
        rgba(
            4,
            120,
            87,
            0.14
        );
}

.uncat-save:hover:not(:disabled) {
    background: #036b4e;
}

.uncat-save:disabled,
.uncat-cancel:disabled {
    cursor: not-allowed;

    opacity: 0.48;
}


/* ========================================================
   RESPONSIVE
======================================================== */

@media (max-width: 900px) {

    .uncat-layout {
        grid-template-columns: 1fr;
    }

    .uncat-editor {
        position: static;
    }

    .uncat-list {
        max-height: 430px;
    }

}


@media (max-width: 560px) {

    .uncat-page {
        padding: 20px 15px 40px;
    }

    .uncat-header {
        flex-direction: column;
    }

    .uncat-total {
        align-items: flex-start;
    }

    .uncat-current,
    .uncat-coordinate-grid {
        grid-template-columns: 1fr;
    }

}

`;